import { useState, useCallback } from 'react';
import type { ProjectFile, ScriptAnalysis } from '@/types/video-enhancer';
import { buildScriptAnalyzeAssets, buildRefineAssets, buildGeneratePlanAssets } from '@/lib/enhancerMeta';

interface UseScriptAnalysisParams {
  files: ProjectFile[];
  analysisResults: Record<string, any>;
  currentProject: string;
  aspectRatio: string;
  toast: (opts: { title: string; description?: string; variant?: string }) => void;
  onCreativeBriefChange: (brief: string) => void;
  applyAIEditToTimeline: (json: any, title: string) => void;
}

export function useScriptAnalysis({
  files,
  analysisResults,
  currentProject,
  aspectRatio,
  toast,
  onCreativeBriefChange,
  applyAIEditToTimeline,
}: UseScriptAnalysisParams) {
  const [scriptContent, setScriptContent] = useState<string | null>(() => {
    try {
      const saved = localStorage.getItem(`project_${currentProject}_scriptContent`);
      return saved || null;
    } catch {
      return null;
    }
  });
  const [scriptFileName, setScriptFileName] = useState<string | null>(() => {
    try {
      return localStorage.getItem(`project_${currentProject}_scriptFileName`) || null;
    } catch {
      return null;
    }
  });
  const [scriptAnalysis, setScriptAnalysis] = useState<ScriptAnalysis | null>(null);
  const [isAnalyzingScript, setIsAnalyzingScript] = useState(false);
  const [scriptAnswers, setScriptAnswers] = useState<Record<string, string>>({});
  const [showScriptPanel, setShowScriptPanel] = useState(false);
  const [isGeneratingScriptPlan, setIsGeneratingScriptPlan] = useState(false);
  const [scriptConversation, setScriptConversation] = useState<Array<{ role: 'ai' | 'user'; content: string; timestamp: number }>>([]);
  const [isRefiningScript, setIsRefiningScript] = useState(false);

  const handleScriptLoaded = useCallback((text: string, fileName?: string) => {
    setScriptContent(text || null);
    setScriptFileName(fileName || null);
    setScriptAnalysis(null);
    setScriptAnswers({});

    if (text) {
      onCreativeBriefChange(text);
    }

    if (text) {
      localStorage.setItem(`project_${currentProject}_scriptContent`, text);
      if (fileName) {
        localStorage.setItem(`project_${currentProject}_scriptFileName`, fileName);
      }
    } else {
      localStorage.removeItem(`project_${currentProject}_scriptContent`);
      localStorage.removeItem(`project_${currentProject}_scriptFileName`);
    }
  }, [currentProject, onCreativeBriefChange]);

  const handleAnalyzeScript = async () => {
    if (!scriptContent || files.length === 0) return;

    setIsAnalyzingScript(true);

    try {
      // Consolidated builder (variant: script-analyze) in enhancerMeta
      const assets_metadata = buildScriptAnalyzeAssets(files, analysisResults);

      const response = await fetch('/api/script/analyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          scriptText: scriptContent,
          assets_metadata,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to analyze script');
      }

      const data = await response.json();

      if (!data.analysis || !data.analysis.scenes) {
        throw new Error('Invalid analysis response - missing scenes');
      }

      setScriptAnalysis(data.analysis);
      setShowScriptPanel(true);

      if (currentProject) {
        localStorage.setItem(`project_${currentProject}_scriptAnalysis`, JSON.stringify(data.analysis));
      }

      toast({
        title: 'Script analyzed',
        description: `Identified ${data.analysis.scenes?.length || 0} scenes`,
      });
    } catch (error) {
      console.error('Script analysis error:', error);
      toast({
        title: 'Analysis failed',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setIsAnalyzingScript(false);
    }
  };

  const handleScriptAnswerChange = (questionId: string, answer: string) => {
    setScriptAnswers(prev => ({ ...prev, [questionId]: answer }));
  };

  const handleScriptRefinement = async (message: string) => {
    if (!scriptAnalysis) return;

    setScriptConversation(prev => [...prev, {
      role: 'user' as const,
      content: message,
      timestamp: Date.now()
    }]);

    setIsRefiningScript(true);

    try {
      // Consolidated builder (variant: refine) in enhancerMeta
      const assets_metadata = buildRefineAssets(files);

      const response = await fetch('/api/script/refine', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          currentAnalysis: scriptAnalysis,
          userFeedback: message,
          conversationHistory: scriptConversation,
          assets_metadata,
          answers: scriptAnswers,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to refine script analysis');
      }

      const data = await response.json();

      setScriptConversation(prev => [...prev, {
        role: 'ai' as const,
        content: data.aiResponse || 'Updated the edit plan based on your feedback.',
        timestamp: Date.now()
      }]);

      if (data.refinedAnalysis) {
        setScriptAnalysis(data.refinedAnalysis);
      }

      toast({
        title: "Analysis Refined",
        description: "The edit plan has been updated based on your feedback.",
      });
    } catch (error) {
      console.error('Error refining script:', error);
      toast({
        title: "Refinement Failed",
        description: "Could not refine the analysis. Please try again.",
        variant: "destructive",
      });
      setScriptConversation(prev => [...prev, {
        role: 'ai' as const,
        content: 'Sorry, I encountered an error processing your request. Please try again.',
        timestamp: Date.now()
      }]);
    } finally {
      setIsRefiningScript(false);
    }
  };

  const handleGenerateScriptPlan = async (selectedClips?: Record<string, number>) => {
    if (!scriptAnalysis) return;

    setIsGeneratingScriptPlan(true);

    try {
      // Consolidated builder (variant: generate-plan) in enhancerMeta
      const assets_metadata = buildGeneratePlanAssets(files);

      let analysisToSend = scriptAnalysis;
      if (selectedClips && Object.keys(selectedClips).length > 0) {
        analysisToSend = {
          ...scriptAnalysis,
          scenes: scriptAnalysis.scenes.map(scene => {
            const selectedIdx = selectedClips[scene.id];
            if (selectedIdx !== undefined && selectedIdx > 0 && scene.suggestedClips?.length > selectedIdx) {
              const clips = [...scene.suggestedClips];
              const [selectedClip] = clips.splice(selectedIdx, 1);
              return { ...scene, suggestedClips: [selectedClip, ...clips] };
            }
            return scene;
          }),
        };
      }

      const response = await fetch('/api/script/generate-plan', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          analysis: analysisToSend,
          answers: scriptAnswers,
          assets_metadata,
          aspect_ratio: aspectRatio,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to generate edit plan');
      }

      const data = await response.json();

      if (data.shotstackJson) {
        applyAIEditToTimeline(data.shotstackJson, 'Edit Plan Applied');
      }
    } catch (error) {
      console.error('Generate plan error:', error);
      toast({
        title: 'Generation failed',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setIsGeneratingScriptPlan(false);
    }
  };

  // Load script data when project changes
  const loadProjectScriptData = (projectName: string) => {
    const savedScriptContent = localStorage.getItem(`project_${projectName}_scriptContent`);
    setScriptContent(savedScriptContent || null);
    const savedScriptFileName = localStorage.getItem(`project_${projectName}_scriptFileName`);
    setScriptFileName(savedScriptFileName || null);
    const savedScriptAnalysis = localStorage.getItem(`project_${projectName}_scriptAnalysis`);
    setScriptAnalysis(savedScriptAnalysis ? JSON.parse(savedScriptAnalysis) : null);
    setScriptAnswers({});
    setScriptConversation([]);
    setIsRefiningScript(false);
  };

  // Reset script state (e.g., on project switch)
  const resetScriptState = () => {
    setScriptContent(null);
    setScriptFileName(null);
    setScriptAnalysis(null);
    setScriptAnswers({});
    setShowScriptPanel(false);
    setScriptConversation([]);
    setIsRefiningScript(false);
  };

  return {
    scriptContent,
    setScriptContent,
    scriptFileName,
    setScriptFileName,
    scriptAnalysis,
    setScriptAnalysis,
    isAnalyzingScript,
    scriptAnswers,
    showScriptPanel,
    setShowScriptPanel,
    isGeneratingScriptPlan,
    scriptConversation,
    isRefiningScript,
    handleScriptLoaded,
    handleAnalyzeScript,
    handleScriptAnswerChange,
    handleScriptRefinement,
    handleGenerateScriptPlan,
    loadProjectScriptData,
    resetScriptState,
  };
}
